import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  LayoutDashboard, 
  Target, 
  Users, 
  FileText, 
  FolderOpen,
  MessageSquare,
  CreditCard,
  Settings,
  LogOut,
  Bell,
  Search,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Eye,
  MousePointer,
  CheckCircle2,
  CheckSquare,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Plus,
  Filter,
  MoreVertical,
  ArrowUpRight,
  Calendar,
  Mail,
  Phone,
  Building2,
  User,
  Menu,
  X,
  Send,
  Paperclip,
  FileIcon,
  Trash2,
  Upload,
  File,
  Image,
  RefreshCw,
  Zap,
  BarChart3,
  Activity,
  ArrowRight,
  Star,
  Info,
  AlertTriangle,
  Play,
  Pause,
  Globe,
  Instagram,
  Facebook,
  MapPin,
  Briefcase,
  ClipboardList
} from 'lucide-react';
import ClientApprovalsView from '../components/client/ClientApprovalsView';
import ClientTasksView from '../components/client/ClientTasksView';
import ClientProjectFilesView from '../components/client/ClientProjectFilesView';

const API = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_client-dash-revamp/artifacts/wn2edy78_logo_transparent_background.png';

// ============ MAIN DASHBOARD ============
export default function ClientDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);

  useEffect(() => {
    fetchClientData();
    fetchPendingApprovals();
    fetchPendingTasks();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const res = await fetch(`${API}/api/client/approvals?status=pending`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPendingApprovals(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Error fetching approvals count:', err);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const res = await fetch(`${API}/api/client/tasks`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPendingTasks(data.counts?.pending || 0);
      }
    } catch (err) {
      console.error('Error fetching tasks count:', err);
    }
  };

  const fetchClientData = async () => {
    try {
      const response = await fetch(`${API}/api/client/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setClient(data);
        setUnreadMessages(data.unread_messages || 0);
      } else {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      navigate('/client/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/client/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      localStorage.removeItem('client_logged_in');
      localStorage.removeItem('client_id');
      localStorage.removeItem('business_name');
      navigate('/client/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={(tab) => {
          setActiveTab(tab);
          setMobileMenuOpen(false);
        }}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onLogout={handleLogout}
        client={client}
        unreadMessages={unreadMessages}
        pendingApprovals={pendingApprovals}
        pendingTasks={pendingTasks}
      />
      
      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Header */}
        <DashboardHeader 
          client={client} 
          setMobileMenuOpen={setMobileMenuOpen}
          unreadMessages={unreadMessages}
          setActiveTab={setActiveTab}
        />
        
        {/* Content Area */}
        <div className="p-4 md:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            {activeTab === 'dashboard' && (
              <DashboardOverview 
                key="dashboard" 
                client={client} 
                setActiveTab={setActiveTab}
              />
            )}
            {activeTab === 'tasks' && <ClientTasksView key="tasks" client={client} setActiveTab={setActiveTab} />}
            {activeTab === 'approvals' && <ClientApprovalsView key="approvals" client={client} />}
            {activeTab === 'campaigns' && <CampaignsView key="campaigns" client={client} />}
            {activeTab === 'leads' && <LeadsView key="leads" client={client} />}
            {activeTab === 'reports' && <ReportsView key="reports" client={client} />}
            {activeTab === 'files' && <ClientProjectFilesView key="files" client={client} clientAssets={client?.assets} onRefresh={fetchClientData} />}
            {activeTab === 'messages' && <MessagesView key="messages" client={client} setUnreadMessages={setUnreadMessages} />}
            {activeTab === 'billing' && <BillingView key="billing" client={client} />}
            {activeTab === 'settings' && <SettingsView key="settings" client={client} />}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

// ============ SIDEBAR ============
function Sidebar({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, mobileMenuOpen, setMobileMenuOpen, onLogout, client, unreadMessages, pendingApprovals, pendingTasks }) {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, description: 'Overview & KPIs' },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList, badge: pendingTasks > 0 ? pendingTasks : null, description: 'Your action items' },
    { id: 'approvals', label: 'Approvals', icon: CheckSquare, badge: pendingApprovals > 0 ? pendingApprovals : null, description: 'Review & approve items' },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: unreadMessages > 0 ? unreadMessages : null, description: 'Chat with your PM' },
    { id: 'files', label: 'Files', icon: FolderOpen, description: 'Shared documents' },
    { id: 'billing', label: 'Billing', icon: CreditCard, description: 'Invoices & payments' },
    { id: 'leads', label: 'Leads', icon: Users, description: 'Lead tracking' },
    { id: 'reports', label: 'Reports', icon: FileText, description: 'Performance reports' },
    { id: 'campaigns', label: 'Campaigns', icon: Target, description: 'Active campaigns' },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-screen bg-white border-r border-slate-200 
        flex-col z-50 transition-all duration-300 hidden lg:flex shadow-sm
        ${sidebarOpen ? 'w-64' : 'w-20'}
      `}>
        {/* Logo */}
        <div className={`p-4 border-b border-slate-100 flex items-center ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
          <div className="flex-shrink-0">
            <img 
              src={LOGO_URL} 
              alt="BirdenMedia" 
              className={`${sidebarOpen ? 'h-10' : 'h-8'} w-auto`}
            />
          </div>
          {sidebarOpen && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="overflow-hidden"
            >
              <p className="font-semibold text-slate-800 text-sm">BirdenMedia</p>
              <p className="text-xs text-slate-400">{client?.short_id}</p>
            </motion.div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
                    ${activeTab === item.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                    ${!sidebarOpen ? 'justify-center' : ''}
                  `}
                  title={!sidebarOpen ? item.label : undefined}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left text-sm">{item.label}</span>
                      {item.badge && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold min-w-[20px] text-center">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {!sidebarOpen && item.badge && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t border-slate-100">
          <button
            onClick={() => setActiveTab('settings')}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 
              hover:bg-slate-50 hover:text-slate-900 transition-all duration-200
              ${!sidebarOpen ? 'justify-center' : ''}
              ${activeTab === 'settings' ? 'bg-slate-50 text-slate-900' : ''}
            `}
          >
            <Settings className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Settings</span>}
          </button>
          <button
            onClick={onLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 
              hover:bg-red-50 hover:text-red-600 transition-all duration-200 mt-1
              ${!sidebarOpen ? 'justify-center' : ''}
            `}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Log Out</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors shadow-sm"
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed left-0 top-0 h-screen w-[280px] bg-white border-r border-slate-200 flex flex-col z-50 lg:hidden shadow-xl"
          >
            {/* Mobile Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="BirdenMedia" className="h-10 w-auto" />
                <div>
                  <p className="font-semibold text-slate-800 text-sm">BirdenMedia</p>
                  <p className="text-xs text-slate-400">{client?.short_id}</p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 py-4 overflow-y-auto">
              <ul className="space-y-1 px-3">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveTab(item.id)}
                      className={`
                        w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200
                        ${activeTab === item.id
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }
                      `}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <div className="flex-1 text-left">
                        <span className="text-sm block">{item.label}</span>
                        <span className="text-xs text-slate-400">{item.description}</span>
                      </div>
                      {item.badge && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Mobile Bottom */}
            <div className="p-3 border-t border-slate-100">
              <button
                onClick={() => setActiveTab('settings')}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all"
              >
                <Settings className="w-5 h-5" />
                <span className="text-sm">Settings</span>
              </button>
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all mt-1"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Log Out</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ HEADER ============
function DashboardHeader({ client, setMobileMenuOpen, unreadMessages, setActiveTab }) {
  return (
    <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800">
              Welcome back, {client?.primary_contact_first_name || 'there'}
            </h1>
            <p className="text-sm text-slate-500">{client?.business_name}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 md:gap-4">
          {/* Search - Hidden on mobile */}
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-48 lg:w-64 pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
          
          {/* Notifications */}
          <button 
            onClick={() => setActiveTab('messages')}
            className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadMessages > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </span>
            )}
          </button>
          
          {/* Profile */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-sm font-semibold text-white">
                {client?.primary_contact_first_name?.charAt(0) || 'C'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// ============ PAGE HEADER COMPONENT ============
function PageHeader({ title, description, icon: Icon, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
        )}
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800">{title}</h2>
          <p className="text-slate-500 text-sm mt-0.5">{description}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

// ============ EMPTY STATE COMPONENT ============
function EmptyState({ icon: Icon, title, description, action, actionLabel }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm"
    >
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">{description}</p>
      {action && (
        <button 
          onClick={action}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
        >
          {actionLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </motion.div>
  );
}

// ============ MODAL COMPONENT ============
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============ DASHBOARD OVERVIEW ============
function DashboardOverview({ client, setActiveTab }) {
  const [animatedStats, setAnimatedStats] = useState({});
  
  // Mock stats - TODO: wire to API when performance endpoints are ready
  const mockStats = {
    total_leads: 47,
    total_spent: 12500,
    avg_cpl: 45.80,
    conversion_rate: 3.2
  };

  useEffect(() => {
    // Animate numbers counting up
    const duration = 1500;
    const steps = 60;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      
      setAnimatedStats({
        total_leads: Math.round(mockStats.total_leads * eased),
        total_spent: Math.round(mockStats.total_spent * eased),
        avg_cpl: (mockStats.avg_cpl * eased).toFixed(2),
        conversion_rate: (mockStats.conversion_rate * eased).toFixed(1)
      });
      
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  const invoices = client?.invoices || [];
  const assets = client?.assets || [];
  const pendingInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'unpaid');

  // Format account manager name with email
  const formatAccountManager = () => {
    const staff = client?.assigned_staff || 'Unassigned';
    const parts = staff.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1]?.charAt(0)}.`;
    }
    return staff;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Leads"
          value={animatedStats.total_leads || 0}
          change="+12% vs last month"
          trend="up"
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Ad Spend"
          value={`$${(animatedStats.total_spent || 0).toLocaleString()}`}
          change="This month"
          trend="neutral"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Cost Per Lead"
          value={`$${animatedStats.avg_cpl || '0.00'}`}
          change="-8% vs last month"
          trend="down"
          icon={Target}
          color="purple"
        />
        <StatCard
          title="Conversion Rate"
          value={`${animatedStats.conversion_rate || '0.0'}%`}
          change="+0.5% vs last month"
          trend="up"
          icon={TrendingUp}
          color="amber"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickActionCard
          title="Messages"
          description={client?.unread_messages > 0 ? `${client.unread_messages} unread messages` : "Chat with your PM"}
          icon={MessageSquare}
          onClick={() => setActiveTab('messages')}
          badge={client?.unread_messages > 0 ? client.unread_messages : null}
        />
        <QuickActionCard
          title="Billing"
          description={pendingInvoices.length > 0 ? `${pendingInvoices.length} invoice(s) pending` : "All invoices paid"}
          icon={CreditCard}
          onClick={() => setActiveTab('billing')}
          badge={pendingInvoices.length > 0 ? pendingInvoices.length : null}
          badgeColor="amber"
        />
        <QuickActionCard
          title="Files"
          description={`${assets.length} shared file(s)`}
          icon={FolderOpen}
          onClick={() => setActiveTab('files')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Status */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-slate-800">Account Overview</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              client?.client_status === 'active' 
                ? 'bg-green-50 text-green-600 border border-green-200' 
                : 'bg-amber-50 text-amber-600 border border-amber-200'
            }`}>
              {client?.client_status || 'Active'}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoBlock label="Plan" value={client?.plan_tier || 'Growth'} />
            <InfoBlock label="Status" value={client?.client_status || 'Active'} />
            <InfoBlock 
              label="Account Manager" 
              value={formatAccountManager()}
              subtext="sarah@birdenmedia.com"
            />
          </div>

          {/* Onboarding Tasks */}
          {client?.onboarding_tasks && client.onboarding_tasks.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h4 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-500" />
                Onboarding Progress
              </h4>
              <div className="space-y-2">
                {client.onboarding_tasks.slice(0, 4).map((task, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      task.is_completed 
                        ? 'bg-green-100 text-green-500' 
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {task.is_completed ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Clock className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <span className={`text-sm ${task.is_completed ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                      {task.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Recent Activity</h3>
          
          <div className="space-y-3">
            <ActivityItem 
              icon={FileText}
              title="Invoice #INV-001 paid"
              time="2 hours ago"
              color="green"
            />
            <ActivityItem 
              icon={MessageSquare}
              title="New message from PM"
              time="Yesterday"
              color="blue"
            />
            <ActivityItem 
              icon={Target}
              title="Campaign optimized"
              time="2 days ago"
              color="purple"
            />
            <ActivityItem 
              icon={Users}
              title="5 new leads received"
              time="3 days ago"
              color="amber"
            />
          </div>
          
          <button 
            onClick={() => setActiveTab('reports')}
            className="w-full mt-4 py-2 text-center text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            View all activity →
          </button>
        </div>
      </div>

      {/* CTA Banner */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 shadow-lg"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Need help with your campaigns?</h3>
              <p className="text-blue-100 text-sm">Schedule a strategy call with your account manager.</p>
            </div>
          </div>
          <button className="flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-blue-50 text-blue-600 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm">
            <Calendar className="w-4 h-4" />
            Book a Call
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ STAT CARD ============
function StatCard({ title, value, change, trend, icon: Icon, color }) {
  const iconColors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
      className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 hover:shadow-md transition-all duration-200"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== 'neutral' && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${
            trend === 'up' ? 'text-green-600' : 'text-green-600'
          }`}>
            {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          </span>
        )}
      </div>
      <p className="text-xl md:text-2xl font-bold text-slate-800 mb-1">{value}</p>
      <p className="text-xs text-slate-500">{title}</p>
      <p className="text-xs text-slate-400 mt-1">{change}</p>
    </motion.div>
  );
}

// ============ QUICK ACTION CARD ============
function QuickActionCard({ title, description, icon: Icon, onClick, badge, badgeColor = 'blue' }) {
  const badgeColors = {
    blue: 'bg-blue-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-green-500 text-white',
  };

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="w-full bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-800">{title}</h4>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        </div>
        {badge && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeColors[badgeColor]}`}>
            {badge}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
      </div>
    </motion.button>
  );
}

// ============ INFO BLOCK ============
function InfoBlock({ label, value, subtext }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-800 capitalize">{value}</p>
      {subtext && <p className="text-xs text-blue-600 mt-0.5">{subtext}</p>}
    </div>
  );
}

// ============ ACTIVITY ITEM ============
function ActivityItem({ icon: Icon, title, time, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 truncate">{title}</p>
        <p className="text-xs text-slate-400">{time}</p>
      </div>
    </div>
  );
}

// ============ MESSAGES VIEW ============
function MessagesView({ client, setUnreadMessages }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignedPM, setAssignedPM] = useState('Project Manager');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    markMessagesRead();
    
    // Set up polling for real-time feel (every 5 seconds)
    pollIntervalRef.current = setInterval(() => {
      fetchMessages(true); // silent fetch
    }, 5000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async (silent = false) => {
    try {
      const response = await fetch(`${API}/api/client/messages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setAssignedPM(data.assigned_pm || 'Project Manager');
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const markMessagesRead = async () => {
    try {
      await fetch(`${API}/api/client/messages/mark-read`, {
        method: 'PATCH',
        credentials: 'include'
      });
      setUnreadMessages(0);
    } catch (error) {
      console.error('Error marking messages read:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/api/client/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message_text: newMessage })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/client/messages/upload-attachment`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Send message with attachment
        await fetch(`${API}/api/client/messages/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            message_text: `📎 Shared file: ${file.name}`,
            attachment_url: data.file_url,
            attachment_filename: data.filename,
            attachment_type: data.file_type
          })
        });
        fetchMessages();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Messages" 
        description={`Chat with ${assignedPM}`}
        icon={MessageSquare}
      />

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col shadow-sm" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
        {/* Chat Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-slate-50">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-slate-800 text-sm">{assignedPM}</p>
            <p className="text-xs text-green-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              Online
            </p>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Start a conversation</h3>
              <p className="text-slate-500 text-sm max-w-sm">
                Send a message to {assignedPM} to get started.
              </p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} isOwn={msg.sender_type === 'client'} />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip"
            />
            <button 
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-50"
            >
              {uploading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="p-2.5 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200"
            >
              {sending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}

// ============ MESSAGE BUBBLE ============
function MessageBubble({ message, isOwn }) {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getFileIcon = (filename) => {
    if (!filename) return FileIcon;
    const ext = filename.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return Image;
    if (ext === 'pdf') return FileText;
    return FileIcon;
  };

  const hasAttachment = message.attachment_url || message.attachment_filename;
  const AttachmentIcon = getFileIcon(message.attachment_filename);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && (
          <p className="text-xs text-slate-500 mb-1 ml-1">{message.sender_name}</p>
        )}
        <div className={`px-4 py-2.5 rounded-2xl ${
          isOwn 
            ? 'bg-blue-500 text-white rounded-br-md' 
            : 'bg-white text-slate-700 rounded-bl-md border border-slate-200 shadow-sm'
        }`}>
          {/* Attachment Display */}
          {hasAttachment && (
            <a 
              href={message.attachment_url?.startsWith('http') ? message.attachment_url : `${API}${message.attachment_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 p-2 rounded-lg mb-2 transition-colors ${
                isOwn 
                  ? 'bg-blue-400/30 hover:bg-blue-400/40' 
                  : 'bg-slate-100 hover:bg-slate-200'
              }`}
            >
              <AttachmentIcon className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm truncate flex-1">{message.attachment_filename || 'Attachment'}</span>
              <Download className="w-4 h-4 flex-shrink-0" />
            </a>
          )}
          <p className="text-sm whitespace-pre-wrap">{message.message_text}</p>
        </div>
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}>
          <p className="text-xs text-slate-400">
            {formatTime(message.timestamp)}
          </p>
          {isOwn && message.read && (
            <CheckCircle2 className="w-3 h-3 text-blue-400" />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ============ FILES VIEW ============
function FilesView({ client, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  const assets = client?.assets || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'other');

    try {
      const response = await fetch(`${API}/api/client/assets/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (assetId) => {
    setDeleting(assetId);
    try {
      const response = await fetch(`${API}/api/client/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (ext) => {
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return Image;
    if (ext === '.pdf') return FileText;
    return File;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Files" 
        description="Upload and manage shared documents"
        icon={FolderOpen}
        action={
          <>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              className="hidden"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip,.mp4"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-sm"
            >
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload File
            </button>
          </>
        }
      />

      {assets.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="No files yet"
          description="Upload documents, images, and other files to share with your account team."
          action={() => fileInputRef.current?.click()}
          actionLabel="Upload your first file"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {assets.map((asset) => {
            const FileIcon = getFileIcon(asset.file_extension);
            return (
              <motion.div
                key={asset.asset_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -2 }}
                className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <FileIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  {asset.uploaded_by === 'client' && (
                    <button
                      onClick={() => handleDelete(asset.asset_id)}
                      disabled={deleting === asset.asset_id}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                    >
                      {deleting === asset.asset_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
                <h4 className="text-sm font-medium text-slate-800 truncate mb-1">{asset.name}</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Uploaded by {asset.uploaded_by === 'client' ? 'you' : asset.uploaded_by_name}
                </p>
                <a
                  href={`${API}${asset.file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

// ============ BILLING VIEW ============
function BillingView({ client }) {
  const [billingSummary, setBillingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchBillingSummary();
  }, []);

  const fetchBillingSummary = async () => {
    try {
      const response = await fetch(`${API}/api/client/billing/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBillingSummary(data);
      }
    } catch (error) {
      console.error('Error fetching billing summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (invoice) => {
    setDownloading(true);
    // Simulate download - in production, this would call an API
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Create a simple receipt text file
    const receiptContent = `
PAYMENT RECEIPT
================
BirdenMedia LLC

Invoice: ${invoice.description || 'N/A'}
Invoice ID: ${invoice.invoice_id || 'N/A'}
Amount Paid: $${invoice.amount?.toLocaleString() || 0}
Date: ${invoice.date || invoice.due_date || new Date().toLocaleDateString()}
Status: ${invoice.status?.toUpperCase()}

Thank you for your business!
================
    `.trim();
    
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${invoice.invoice_id?.slice(0, 8) || 'invoice'}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    setDownloading(false);
  };

  const invoices = client?.invoices || [];
  const hasOverdue = invoices.some(inv => inv.status === 'overdue');
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');
  const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const pendingInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'unpaid');

  // Generate mock line items for invoice detail
  const generateLineItems = (invoice) => {
    const baseAmount = invoice.amount || 0;
    const items = [];
    
    if (invoice.description?.toLowerCase().includes('retainer') || invoice.description?.toLowerCase().includes('monthly')) {
      items.push({ description: 'Monthly Marketing Retainer', amount: baseAmount * 0.7 });
      items.push({ description: 'Ad Management Fee', amount: baseAmount * 0.2 });
      items.push({ description: 'Reporting & Analytics', amount: baseAmount * 0.1 });
    } else {
      items.push({ description: invoice.description || 'Service Fee', amount: baseAmount });
    }
    
    return items;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Billing" 
        description="View invoices and payment history"
        icon={CreditCard}
        action={
          pendingInvoices.length > 0 && (
            <button 
              onClick={() => setSelectedInvoice(pendingInvoices[0])}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-all shadow-sm"
            >
              <CreditCard className="w-4 h-4" />
              Pay Outstanding
            </button>
          )
        }
      />

      {/* Overdue Alert Banner */}
      {hasOverdue && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-red-800">Payment Overdue</h4>
            <p className="text-sm text-red-700">
              You have <span className="font-bold">${overdueAmount.toLocaleString()}</span> in overdue payments across {overdueInvoices.length} invoice{overdueInvoices.length > 1 ? 's' : ''}. Please settle your balance to avoid service interruption.
            </p>
          </div>
          <button 
            onClick={() => setSelectedInvoice(overdueInvoices[0])}
            className="px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm flex items-center gap-2 whitespace-nowrap"
          >
            <CreditCard className="w-4 h-4" />
            Pay Now
          </button>
        </motion.div>
      )}

      {/* Billing Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xs text-slate-500">Current Plan</p>
          </div>
          <p className="text-xl font-bold text-slate-800 capitalize">{client?.plan_tier || 'Growth'}</p>
          <p className="text-xs text-slate-400 mt-1">${client?.monthly_fee || 0}/month</p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-xs text-slate-500">Total Paid</p>
          </div>
          <p className="text-xl font-bold text-green-600">${billingSummary?.total_paid?.toLocaleString() || 0}</p>
          <p className="text-xs text-slate-400 mt-1">{billingSummary?.paid_invoice_count || 0} invoices</p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-xs text-slate-500">Outstanding</p>
          </div>
          <p className="text-xl font-bold text-amber-500">${billingSummary?.outstanding_balance?.toLocaleString() || 0}</p>
          <p className="text-xs text-slate-400 mt-1">Pending payment</p>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-xs text-slate-500">Next Invoice</p>
          </div>
          <p className="text-xl font-bold text-slate-800">
            {billingSummary?.next_invoice ? `$${billingSummary.next_invoice.amount}` : '—'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {billingSummary?.next_invoice?.due_date || 'No upcoming'}
          </p>
        </motion.div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Invoice History</h3>
          <span className="text-sm text-slate-500">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
        </div>
        
        {invoices.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No invoices yet</h3>
            <p className="text-slate-500 text-sm">Your invoices will appear here once generated.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Due Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice, idx) => (
                  <motion.tr 
                    key={idx} 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          invoice.status === 'paid' ? 'bg-green-50' : 
                          invoice.status === 'overdue' ? 'bg-red-50' : 'bg-amber-50'
                        }`}>
                          <FileText className={`w-5 h-5 ${
                            invoice.status === 'paid' ? 'text-green-500' : 
                            invoice.status === 'overdue' ? 'text-red-500' : 'text-amber-500'
                          }`} />
                        </div>
                        <div>
                          <span className="font-medium text-slate-800 block">{invoice.description || `INV-${idx + 1}`}</span>
                          {invoice.invoice_id && (
                            <span className="text-xs text-slate-400 font-mono">{invoice.invoice_id.slice(0, 8).toUpperCase()}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-800 font-semibold">${invoice.amount?.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                        invoice.status === 'paid' 
                          ? 'bg-green-50 text-green-600' 
                          : invoice.status === 'overdue'
                          ? 'bg-red-50 text-red-600'
                          : 'bg-amber-50 text-amber-600'
                      }`}>
                        {invoice.status === 'paid' ? '✓ Paid' : invoice.status === 'overdue' ? '⚠ Overdue' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-sm hidden sm:table-cell">{invoice.due_date || '—'}</td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedInvoice(invoice); }}
                        className={`text-sm font-medium transition-colors ${
                          invoice.status === 'paid' 
                            ? 'text-slate-600 hover:text-slate-800' 
                            : 'text-blue-600 hover:text-blue-700'
                        }`}
                      >
                        {invoice.status === 'paid' ? 'View' : 'Pay Now'}
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Methods Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex items-center gap-4">
        <Info className="w-5 h-5 text-slate-400 flex-shrink-0" />
        <p className="text-sm text-slate-600">
          We accept all major credit cards, debit cards, and bank transfers. All payments are securely processed.
        </p>
        <div className="flex items-center gap-2 ml-auto">
          <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center text-xs font-bold text-blue-600">VISA</div>
          <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center text-xs font-bold text-red-500">MC</div>
          <div className="w-10 h-6 bg-white rounded border border-slate-200 flex items-center justify-center text-xs font-bold text-blue-700">AMEX</div>
        </div>
      </div>

      {/* Invoice Modal */}
      <Modal 
        isOpen={!!selectedInvoice} 
        onClose={() => setSelectedInvoice(null)}
        title={selectedInvoice?.status === 'paid' ? 'Invoice Receipt' : 'Pay Invoice'}
      >
        {selectedInvoice && (
          <div className="space-y-5">
            {/* Invoice Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
              <div>
                <p className="text-sm text-slate-500">Invoice</p>
                <p className="text-lg font-bold text-slate-800">{selectedInvoice.description}</p>
                {selectedInvoice.invoice_id && (
                  <p className="text-xs text-slate-400 font-mono mt-1">#{selectedInvoice.invoice_id.slice(0, 8).toUpperCase()}</p>
                )}
              </div>
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                selectedInvoice.status === 'paid' 
                  ? 'bg-green-50 text-green-600' 
                  : selectedInvoice.status === 'overdue'
                  ? 'bg-red-50 text-red-600'
                  : 'bg-amber-50 text-amber-600'
              }`}>
                {selectedInvoice.status === 'paid' ? '✓ Paid' : selectedInvoice.status === 'overdue' ? '⚠ Overdue' : 'Pending'}
              </span>
            </div>

            {/* Line Items Breakdown */}
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Invoice Details</p>
              <div className="space-y-2">
                {generateLineItems(selectedInvoice).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                    <span className="text-sm text-slate-700">{item.description}</span>
                    <span className="text-sm font-medium text-slate-800">${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-slate-300">
                <span className="font-semibold text-slate-800">Total Amount</span>
                <span className="font-bold text-xl text-slate-800">${selectedInvoice.amount?.toLocaleString()}</span>
              </div>
            </div>

            {/* Invoice Meta */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">Invoice Date</p>
                <p className="text-sm font-medium text-slate-800">{selectedInvoice.date || new Date().toLocaleDateString()}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-xs text-slate-500">Due Date</p>
                <p className="text-sm font-medium text-slate-800">{selectedInvoice.due_date || '—'}</p>
              </div>
            </div>

            {/* Payment Actions */}
            {selectedInvoice.status !== 'paid' ? (
              <div className="space-y-3">
                {/* Credit Card Payment */}
                <button className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25">
                  <CreditCard className="w-5 h-5" />
                  Pay ${selectedInvoice.amount?.toLocaleString()} with Card
                </button>
                
                {/* Alternative Payment Methods */}
                <div className="grid grid-cols-2 gap-3">
                  <button className="py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Bank Transfer
                  </button>
                  <button className="py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                </div>
                
                <p className="text-xs text-center text-slate-400">
                  Secure payment powered by Stripe. Your card details are never stored.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">Payment Complete</p>
                  <p className="text-sm text-green-600">Thank you for your payment</p>
                  {selectedInvoice.paid_date && (
                    <p className="text-xs text-green-500 mt-1">Paid on {selectedInvoice.paid_date}</p>
                  )}
                </div>
                <button 
                  onClick={() => handleDownloadReceipt(selectedInvoice)}
                  disabled={downloading}
                  className="w-full py-3 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-100 text-slate-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {downloading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download Receipt
                </button>
              </div>
            )}

            {/* Contact Support */}
            <div className="text-center pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                Questions about this invoice? <button className="text-blue-500 hover:text-blue-600 font-medium">Contact your account manager</button>
              </p>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}

// ============ LEADS VIEW ============
function LeadsView({ client }) {
  const [leads, setLeads] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ new: 0, reviewed: 0, contact_initiated: 0, exported: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [updatingLead, setUpdatingLead] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [activeFilter]);

  const fetchLeads = async () => {
    try {
      const statusParam = activeFilter !== 'all' ? `?status=${activeFilter}` : '';
      const response = await fetch(`${API}/api/client/leads${statusParam}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setStatusCounts(data.status_counts || { new: 0, reviewed: 0, contact_initiated: 0, exported: 0 });
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLead = async (lead) => {
    // When viewing a lead, it gets marked as reviewed by the API
    try {
      const response = await fetch(`${API}/api/client/leads/${lead.lead_id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const updatedLead = await response.json();
        setSelectedLead(updatedLead);
        // Refresh counts if status changed
        if (lead.status === 'new' && updatedLead.status === 'reviewed') {
          fetchLeads();
        }
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      setSelectedLead(lead); // Fall back to existing data
    }
  };

  const handleMarkContacted = async (leadId) => {
    setUpdatingLead(leadId);
    try {
      const response = await fetch(`${API}/api/client/leads/${leadId}/contact-initiated`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        fetchLeads();
        if (selectedLead && selectedLead.lead_id === leadId) {
          setSelectedLead({ ...selectedLead, status: 'contact_initiated', contact_initiated_at: new Date().toISOString() });
        }
      }
    } catch (error) {
      console.error('Error marking lead as contacted:', error);
    } finally {
      setUpdatingLead(null);
    }
  };

  const handleExportLead = async (leadId) => {
    setUpdatingLead(leadId);
    try {
      const response = await fetch(`${API}/api/client/leads/${leadId}/export`, {
        method: 'PATCH',
        credentials: 'include'
      });
      if (response.ok) {
        fetchLeads();
        if (selectedLead && selectedLead.lead_id === leadId) {
          setSelectedLead({ ...selectedLead, status: 'exported', exported_at: new Date().toISOString() });
        }
      }
    } catch (error) {
      console.error('Error marking lead as exported:', error);
    } finally {
      setUpdatingLead(null);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/api/client/leads/export?format=csv`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        // Trigger file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        fetchLeads(); // Refresh to show updated statuses
      }
    } catch (error) {
      console.error('Error exporting leads:', error);
    } finally {
      setExporting(false);
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'facebook_ads': return Facebook;
      case 'instagram': return Instagram;
      case 'google_ads': return Globe;
      case 'website': return Globe;
      default: return Globe;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'facebook_ads': return 'Facebook';
      case 'instagram': return 'Instagram';
      case 'google_ads': return 'Google Ads';
      case 'website': return 'Website';
      case 'referral': return 'Referral';
      default: return 'Other';
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'facebook_ads': return 'bg-blue-50 text-blue-600';
      case 'instagram': return 'bg-pink-50 text-pink-600';
      case 'google_ads': return 'bg-green-50 text-green-600';
      case 'website': return 'bg-purple-50 text-purple-600';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'new':
        return { 
          color: 'bg-blue-50 text-blue-600 border-blue-200', 
          label: 'New',
          tooltip: 'Fresh lead - not yet viewed'
        };
      case 'reviewed':
        return { 
          color: 'bg-amber-50 text-amber-600 border-amber-200', 
          label: 'Reviewed',
          tooltip: 'You have viewed this lead\'s details'
        };
      case 'contact_initiated':
        return { 
          color: 'bg-green-50 text-green-600 border-green-200', 
          label: 'Contacted',
          tooltip: 'You\'ve reached out to this lead'
        };
      case 'exported':
        return { 
          color: 'bg-slate-100 text-slate-600 border-slate-200', 
          label: 'Exported',
          tooltip: 'Lead data has been exported'
        };
      default:
        return { 
          color: 'bg-slate-50 text-slate-600 border-slate-200', 
          label: status,
          tooltip: ''
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const totalLeads = statusCounts.new + statusCounts.reviewed + statusCounts.contact_initiated + statusCounts.exported;

  const filterTabs = [
    { id: 'all', label: 'All', count: totalLeads },
    { id: 'new', label: 'New', count: statusCounts.new, color: 'blue' },
    { id: 'reviewed', label: 'Reviewed', count: statusCounts.reviewed, color: 'amber' },
    { id: 'contact_initiated', label: 'Contacted', count: statusCounts.contact_initiated, color: 'green' },
    { id: 'exported', label: 'Exported', count: statusCounts.exported, color: 'slate' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Leads" 
        description="Track and manage leads from your campaigns"
        icon={Users}
        action={
          <button 
            onClick={handleExportAll}
            disabled={exporting || totalLeads === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 text-white text-sm font-medium rounded-xl transition-all shadow-sm"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export All
          </button>
        }
      />

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {filterTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeFilter === tab.id
                ? 'bg-blue-500 text-white shadow-sm'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {tab.label}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              activeFilter === tab.id
                ? 'bg-white/20 text-white'
                : tab.color === 'blue' ? 'bg-blue-100 text-blue-600'
                : tab.color === 'amber' ? 'bg-amber-100 text-amber-600'
                : tab.color === 'green' ? 'bg-green-100 text-green-600'
                : 'bg-slate-100 text-slate-600'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Empty State */}
      {leads.length === 0 ? (
        <EmptyState
          icon={Users}
          title={activeFilter === 'all' ? "No leads yet" : `No ${activeFilter.replace('_', ' ')} leads`}
          description={activeFilter === 'all' 
            ? "Leads from your campaigns will appear here as they come in."
            : `You don't have any leads with "${activeFilter.replace('_', ' ')}" status.`
          }
          action={activeFilter !== 'all' ? () => setActiveFilter('all') : null}
          actionLabel="View all leads"
        />
      ) : (
        /* Leads Table */
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Lead</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Source</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Submitted</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead) => {
                  const SourceIcon = getSourceIcon(lead.source);
                  const statusConfig = getStatusConfig(lead.status);
                  return (
                    <motion.tr 
                      key={lead.lead_id} 
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => handleViewLead(lead)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            {lead.first_name?.charAt(0)}{lead.last_name?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">{lead.first_name} {lead.last_name}</p>
                            <p className="text-xs text-slate-500 truncate">{lead.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${getSourceColor(lead.source)}`}>
                          <SourceIcon className="w-3.5 h-3.5" />
                          {getSourceLabel(lead.source)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative">
                          <span 
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusConfig.color} cursor-help`}
                            onMouseEnter={() => setShowTooltip(lead.lead_id)}
                            onMouseLeave={() => setShowTooltip(null)}
                          >
                            {statusConfig.label}
                          </span>
                          {/* Tooltip */}
                          {showTooltip === lead.lead_id && statusConfig.tooltip && (
                            <div className="absolute z-10 bottom-full left-0 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap shadow-lg">
                              {statusConfig.tooltip}
                              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-sm hidden sm:table-cell">
                        {formatDate(lead.submitted_at)}
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleViewLead(lead); }}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      <Modal
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        title="Lead Details"
      >
        {selectedLead && (
          <div className="space-y-4">
            {/* Lead Header */}
            <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {selectedLead.first_name?.charAt(0)}{selectedLead.last_name?.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-800 text-lg truncate">
                  {selectedLead.first_name} {selectedLead.last_name}
                </h3>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${getSourceColor(selectedLead.source)}`}>
                    {React.createElement(getSourceIcon(selectedLead.source), { className: 'w-3.5 h-3.5' })}
                    {getSourceLabel(selectedLead.source)}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusConfig(selectedLead.status).color}`}>
                    {getStatusConfig(selectedLead.status).label}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <a 
                href={`mailto:${selectedLead.email}`}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <Mail className="w-4 h-4 text-blue-500" />
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-sm font-medium text-slate-800 truncate">{selectedLead.email}</p>
                </div>
              </a>
              <a 
                href={`tel:${selectedLead.phone}`}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <Phone className="w-4 h-4 text-green-500" />
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">Phone</p>
                  <p className="text-sm font-medium text-slate-800">{selectedLead.phone}</p>
                </div>
              </a>
              {selectedLead.location && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="text-sm font-medium text-slate-800">{selectedLead.location}</p>
                  </div>
                </div>
              )}
              {selectedLead.service_requested && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Service Requested</p>
                    <p className="text-sm font-medium text-slate-800">{selectedLead.service_requested}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Campaign Info */}
            {selectedLead.campaign_name && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                <Target className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-blue-600">Campaign</p>
                  <p className="text-sm font-medium text-slate-800">{selectedLead.campaign_name}</p>
                </div>
              </div>
            )}

            {/* Landing Page */}
            {selectedLead.landing_page && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 mb-1">Landing Page</p>
                <a 
                  href={selectedLead.landing_page} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 break-all"
                >
                  {selectedLead.landing_page}
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                </a>
              </div>
            )}

            {/* Message/Notes */}
            {selectedLead.message && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-600 font-medium mb-1">Lead Message</p>
                <p className="text-sm text-amber-800">{selectedLead.message}</p>
              </div>
            )}

            {/* Timeline */}
            <div className="p-4 bg-slate-50 rounded-xl space-y-3">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Timeline</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Submitted</span>
                  <span className="text-slate-800 font-medium">{formatDate(selectedLead.submitted_at)}</span>
                </div>
                {selectedLead.reviewed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Reviewed</span>
                    <span className="text-slate-800 font-medium">{formatDate(selectedLead.reviewed_at)}</span>
                  </div>
                )}
                {selectedLead.contact_initiated_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Contact Initiated</span>
                    <span className="text-slate-800 font-medium">{formatDate(selectedLead.contact_initiated_at)}</span>
                  </div>
                )}
                {selectedLead.exported_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Exported</span>
                    <span className="text-slate-800 font-medium">{formatDate(selectedLead.exported_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {selectedLead.status !== 'contact_initiated' && selectedLead.status !== 'exported' && (
                <button
                  onClick={() => handleMarkContacted(selectedLead.lead_id)}
                  disabled={updatingLead === selectedLead.lead_id}
                  className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {updatingLead === selectedLead.lead_id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Phone className="w-4 h-4" />
                  )}
                  Mark as Contacted
                </button>
              )}
              {selectedLead.status !== 'exported' && (
                <button
                  onClick={() => handleExportLead(selectedLead.lead_id)}
                  disabled={updatingLead === selectedLead.lead_id}
                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-100 text-slate-700 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {updatingLead === selectedLead.lead_id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export Lead
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}

// ============ REPORTS VIEW ============
function ReportsView({ client }) {
  const [dateRange, setDateRange] = useState('30d');
  const [activeMetric, setActiveMetric] = useState('leads');

  // Mock data - designed to be replaced with Google Analytics / Meta API data later
  // Staff can configure data sources via admin panel
  const performanceData = [
    { date: 'Dec 1', leads: 12, spend: 850, impressions: 24500, clicks: 580, ctr: 2.37 },
    { date: 'Dec 5', leads: 18, spend: 920, impressions: 28400, clicks: 720, ctr: 2.53 },
    { date: 'Dec 10', leads: 15, spend: 780, impressions: 22100, clicks: 510, ctr: 2.31 },
    { date: 'Dec 15', leads: 22, spend: 1100, impressions: 32500, clicks: 890, ctr: 2.74 },
    { date: 'Dec 20', leads: 28, spend: 1250, impressions: 38200, clicks: 1020, ctr: 2.67 },
    { date: 'Dec 25', leads: 19, spend: 980, impressions: 29800, clicks: 780, ctr: 2.62 },
    { date: 'Dec 30', leads: 24, spend: 1150, impressions: 35100, clicks: 920, ctr: 2.62 },
  ];

  const leadSourceData = [
    { name: 'Google Ads', value: 42, color: '#4285F4' },
    { name: 'Facebook', value: 28, color: '#1877F2' },
    { name: 'Instagram', value: 18, color: '#E4405F' },
    { name: 'Website', value: 12, color: '#6366F1' },
  ];

  const campaignPerformance = [
    { name: 'Summer Promo', leads: 28, spend: 1847, cpl: 65.96, status: 'active' },
    { name: 'Brand Awareness', leads: 42, spend: 2890, cpl: 68.81, status: 'active' },
    { name: 'Holiday Special', leads: 15, spend: 1200, cpl: 80.00, status: 'paused' },
    { name: 'Retargeting', leads: 22, spend: 980, cpl: 44.55, status: 'active' },
  ];

  const monthlyTrend = [
    { month: 'Jul', leads: 85, spend: 4200 },
    { month: 'Aug', leads: 92, spend: 4800 },
    { month: 'Sep', leads: 78, spend: 4100 },
    { month: 'Oct', leads: 110, spend: 5200 },
    { month: 'Nov', leads: 125, spend: 5800 },
    { month: 'Dec', leads: 138, spend: 6030 },
  ];

  // Summary stats
  const totalLeads = performanceData.reduce((sum, d) => sum + d.leads, 0);
  const totalSpend = performanceData.reduce((sum, d) => sum + d.spend, 0);
  const avgCPL = totalSpend / totalLeads;
  const totalImpressions = performanceData.reduce((sum, d) => sum + d.impressions, 0);
  const totalClicks = performanceData.reduce((sum, d) => sum + d.clicks, 0);
  const avgCTR = (totalClicks / totalImpressions * 100).toFixed(2);

  const dateRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'ytd', label: 'Year to date' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3">
          <p className="font-semibold text-slate-800 text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Spend') ? `$${entry.value.toLocaleString()}` : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader 
          title="Reports & Analytics" 
          description="Track your campaign performance and ROI"
          icon={BarChart3}
        />
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            {dateRangeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-xl transition-all">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xs text-slate-500">Total Leads</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totalLeads}</p>
          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" /> +12% vs last period
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-xs text-slate-500">Total Spend</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">${totalSpend.toLocaleString()}</p>
          <p className="text-xs text-slate-400 mt-1">This period</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-xs text-slate-500">Avg. CPL</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">${avgCPL.toFixed(2)}</p>
          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
            <TrendingDown className="w-3 h-3" /> -8% vs last period
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
              <Eye className="w-4 h-4 text-amber-500" />
            </div>
            <p className="text-xs text-slate-500">Impressions</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{(totalImpressions / 1000).toFixed(1)}K</p>
          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" /> +18% vs last period
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-cyan-50 rounded-lg flex items-center justify-center">
              <MousePointer className="w-4 h-4 text-cyan-500" />
            </div>
            <p className="text-xs text-slate-500">Avg. CTR</p>
          </div>
          <p className="text-2xl font-bold text-slate-800">{avgCTR}%</p>
          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" /> +0.3% vs last period
          </p>
        </motion.div>
      </div>

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Over Time */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Performance Over Time</h3>
            <div className="flex gap-1">
              {['leads', 'spend', 'ctr'].map(metric => (
                <button
                  key={metric}
                  onClick={() => setActiveMetric(metric)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${
                    activeMetric === metric 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {metric === 'leads' ? 'Leads' : metric === 'spend' ? 'Spend' : 'CTR'}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorMetric" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748B' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey={activeMetric} 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorMetric)" 
                  name={activeMetric === 'leads' ? 'Leads' : activeMetric === 'spend' ? 'Spend ($)' : 'CTR (%)'}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Lead Sources Pie Chart */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
        >
          <h3 className="font-semibold text-slate-800 mb-4">Lead Sources</h3>
          <div className="h-64 flex items-center">
            <div className="w-1/2">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={leadSourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {leadSourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-1/2 space-y-3">
              {leadSourceData.map((source, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: source.color }} />
                    <span className="text-sm text-slate-600">{source.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{source.value}%</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            Data source: Connected ad platforms
          </p>
        </motion.div>
      </div>

      {/* Monthly Trend */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
      >
        <h3 className="font-semibold text-slate-800 mb-4">Monthly Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748B' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar yAxisId="left" dataKey="leads" fill="#3B82F6" name="Leads" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="spend" fill="#10B981" name="Spend ($)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Campaign Performance Table */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Campaign Performance</h3>
          <span className="text-sm text-slate-500">{campaignPerformance.length} campaigns</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Campaign</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Leads</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Spend</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">CPL</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaignPerformance.map((campaign, idx) => (
                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-medium text-slate-800">{campaign.name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-800">{campaign.leads}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-800">${campaign.spend.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`font-semibold ${campaign.cpl < 60 ? 'text-green-600' : campaign.cpl < 75 ? 'text-amber-600' : 'text-red-600'}`}>
                      ${campaign.cpl.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                      campaign.status === 'active' 
                        ? 'bg-green-50 text-green-600' 
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {campaign.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Data Sources Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-4">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Info className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-blue-800 mb-1">Data Sources</h4>
          <p className="text-sm text-blue-700">
            This dashboard displays aggregated performance data from your connected ad platforms. 
            Your account manager can connect additional data sources including Google Analytics, Meta Ads, 
            and other platforms to provide more comprehensive reporting.
          </p>
          <button className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
            Request data source integration
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============ CAMPAIGNS VIEW ============
function CampaignsView({ client }) {
  const [animatedMetrics, setAnimatedMetrics] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Sample campaigns data
  const sampleCampaigns = [
    {
      id: '1',
      name: 'Summer Promo 2024',
      platform: 'Facebook',
      status: 'active',
      budget: 2500,
      spent: 1847,
      impressions: 145200,
      clicks: 3420,
      conversions: 28,
      cpl: 65.96,
      ctr: 2.36,
      lastUpdated: '2 min ago',
      isLive: true,
      dailyData: [
        { day: 'Mon', impressions: 18500, clicks: 420, conversions: 3, spend: 245 },
        { day: 'Tue', impressions: 21200, clicks: 510, conversions: 4, spend: 280 },
        { day: 'Wed', impressions: 19800, clicks: 480, conversions: 4, spend: 265 },
        { day: 'Thu', impressions: 22100, clicks: 530, conversions: 5, spend: 295 },
        { day: 'Fri', impressions: 24500, clicks: 580, conversions: 5, spend: 310 },
        { day: 'Sat', impressions: 20300, clicks: 490, conversions: 4, spend: 272 },
        { day: 'Sun', impressions: 18800, clicks: 410, conversions: 3, spend: 180 }
      ]
    },
    {
      id: '2',
      name: 'Brand Awareness Q4',
      platform: 'Google Ads',
      status: 'active',
      budget: 3500,
      spent: 2890,
      impressions: 284500,
      clicks: 5670,
      conversions: 42,
      cpl: 68.81,
      ctr: 1.99,
      lastUpdated: '5 min ago',
      isLive: true,
      dailyData: [
        { day: 'Mon', impressions: 38500, clicks: 780, conversions: 6, spend: 395 },
        { day: 'Tue', impressions: 42200, clicks: 850, conversions: 7, spend: 435 },
        { day: 'Wed', impressions: 39800, clicks: 810, conversions: 6, spend: 410 },
        { day: 'Thu', impressions: 44100, clicks: 890, conversions: 7, spend: 450 },
        { day: 'Fri', impressions: 46500, clicks: 920, conversions: 8, spend: 470 },
        { day: 'Sat', impressions: 38300, clicks: 760, conversions: 5, spend: 380 },
        { day: 'Sun', impressions: 35100, clicks: 660, conversions: 3, spend: 350 }
      ]
    },
    {
      id: '3',
      name: 'Holiday Special',
      platform: 'Instagram',
      status: 'active',
      budget: 1800,
      spent: 1234,
      impressions: 98700,
      clicks: 2890,
      conversions: 19,
      cpl: 64.95,
      ctr: 2.93,
      lastUpdated: '8 min ago',
      isLive: true,
      dailyData: [
        { day: 'Mon', impressions: 12500, clicks: 380, conversions: 2, spend: 165 },
        { day: 'Tue', impressions: 14200, clicks: 430, conversions: 3, spend: 185 },
        { day: 'Wed', impressions: 13800, clicks: 410, conversions: 3, spend: 175 },
        { day: 'Thu', impressions: 15100, clicks: 450, conversions: 3, spend: 195 },
        { day: 'Fri', impressions: 16500, clicks: 490, conversions: 4, spend: 210 },
        { day: 'Sat', impressions: 14300, clicks: 420, conversions: 2, spend: 175 },
        { day: 'Sun', impressions: 12300, clicks: 310, conversions: 2, spend: 129 }
      ]
    }
  ];

  useEffect(() => {
    // Animate campaign metrics
    const duration = 2000;
    const steps = 80;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const metrics = {};
      sampleCampaigns.forEach(campaign => {
        metrics[campaign.id] = {
          impressions: Math.round(campaign.impressions * eased),
          clicks: Math.round(campaign.clicks * eased),
          conversions: Math.round(campaign.conversions * eased),
          spent: Math.round(campaign.spent * eased)
        };
      });
      setAnimatedMetrics(metrics);
      
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  const getPlatformColor = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'instagram': return 'bg-pink-50 text-pink-600 border-pink-200';
      case 'google ads': return 'bg-green-50 text-green-600 border-green-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return Facebook;
      case 'instagram': return Instagram;
      case 'google ads': return Globe;
      default: return Globe;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Campaigns" 
        description="Monitor your advertising campaigns in real-time"
        icon={Target}
      />

      <div className="grid gap-6">
        {sampleCampaigns.map((campaign, idx) => {
          const PlatformIcon = getPlatformIcon(campaign.platform);
          const metrics = animatedMetrics[campaign.id] || { impressions: 0, clicks: 0, conversions: 0, spent: 0 };
          
          return (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -2 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
            >
              {/* Campaign Header */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getPlatformColor(campaign.platform)}`}>
                    <PlatformIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-slate-800">{campaign.name}</h3>
                      {campaign.isLive && (
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ repeat: Infinity, duration: 2 }}
                          className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full"
                        >
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          <span className="text-xs font-semibold text-green-600">LIVE</span>
                        </motion.div>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPlatformColor(campaign.platform)}`}>
                        {campaign.platform}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        Updated {campaign.lastUpdated}
                      </span>
                    </p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-xs text-slate-500">Budget</p>
                  <p className="text-lg font-bold text-slate-800">${campaign.budget.toLocaleString()}</p>
                  <div className="w-24 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(metrics.spent / campaign.budget) * 100}%` }}
                      transition={{ duration: 2, ease: 'easeOut' }}
                      className="h-full bg-blue-500 rounded-full"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">${metrics.spent.toLocaleString()} spent</p>
                </div>
              </div>

              {/* Campaign Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <motion.div 
                  className="bg-slate-50 rounded-xl p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-500">Impressions</p>
                  </div>
                  <p className="text-xl font-bold text-slate-800">{metrics.impressions.toLocaleString()}</p>
                </motion.div>
                
                <motion.div 
                  className="bg-slate-50 rounded-xl p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointer className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-500">Clicks</p>
                  </div>
                  <p className="text-xl font-bold text-slate-800">{metrics.clicks.toLocaleString()}</p>
                </motion.div>
                
                <motion.div 
                  className="bg-slate-50 rounded-xl p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-500">Conversions</p>
                  </div>
                  <p className="text-xl font-bold text-slate-800">{metrics.conversions}</p>
                </motion.div>
                
                <motion.div 
                  className="bg-green-50 rounded-xl p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <p className="text-xs text-green-600">Cost/Lead</p>
                  </div>
                  <p className="text-xl font-bold text-green-600">${campaign.cpl.toFixed(2)}</p>
                </motion.div>
                
                <motion.div 
                  className="bg-blue-50 rounded-xl p-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    <p className="text-xs text-blue-600">CTR</p>
                  </div>
                  <p className="text-xl font-bold text-blue-600">{campaign.ctr}%</p>
                </motion.div>
              </div>

              {/* View Charts Toggle */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <button
                  onClick={() => setSelectedCampaign(selectedCampaign === campaign.id ? null : campaign.id)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  <BarChart3 className="w-4 h-4" />
                  {selectedCampaign === campaign.id ? 'Hide Performance Charts' : 'View Performance Charts'}
                  <ChevronRight className={`w-4 h-4 transition-transform ${selectedCampaign === campaign.id ? 'rotate-90' : ''}`} />
                </button>
              </div>

              {/* Expandable Charts Section */}
              <AnimatePresence>
                {selectedCampaign === campaign.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="text-sm font-semibold text-slate-700 mb-4">7-Day Performance</h4>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Impressions & Clicks Chart */}
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-medium text-slate-500 mb-3">Impressions & Clicks</p>
                          <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={campaign.dailyData}>
                                <defs>
                                  <linearGradient id={`impressions-${campaign.id}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                  </linearGradient>
                                  <linearGradient id={`clicks-${campaign.id}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'white', 
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                  }}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="impressions" 
                                  stroke="#3b82f6" 
                                  strokeWidth={2}
                                  fill={`url(#impressions-${campaign.id})`}
                                  name="Impressions"
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="clicks" 
                                  stroke="#10b981" 
                                  strokeWidth={2}
                                  fill={`url(#clicks-${campaign.id})`}
                                  name="Clicks"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>

                        {/* Conversions & Spend Chart */}
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-xs font-medium text-slate-500 mb-3">Conversions & Daily Spend</p>
                          <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={campaign.dailyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'white', 
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                  }}
                                  formatter={(value, name) => [
                                    name === 'Spend' ? `$${value}` : value,
                                    name
                                  ]}
                                />
                                <Bar 
                                  yAxisId="left"
                                  dataKey="conversions" 
                                  fill="#8b5cf6" 
                                  radius={[4, 4, 0, 0]}
                                  name="Conversions"
                                />
                                <Line 
                                  yAxisId="right"
                                  type="monotone" 
                                  dataKey="spend" 
                                  stroke="#f59e0b" 
                                  strokeWidth={2}
                                  dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                                  name="Spend"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>

                      {/* Performance Summary */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Avg. Daily Impressions</p>
                          <p className="text-lg font-bold text-slate-800">
                            {Math.round(campaign.dailyData.reduce((sum, d) => sum + d.impressions, 0) / 7).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Avg. Daily Clicks</p>
                          <p className="text-lg font-bold text-slate-800">
                            {Math.round(campaign.dailyData.reduce((sum, d) => sum + d.clicks, 0) / 7).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Best Day</p>
                          <p className="text-lg font-bold text-green-600">
                            {campaign.dailyData.reduce((best, d) => d.conversions > best.conversions ? d : best).day}
                          </p>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Weekly Conversions</p>
                          <p className="text-lg font-bold text-purple-600">
                            {campaign.dailyData.reduce((sum, d) => sum + d.conversions, 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ============ SETTINGS VIEW ============
function SettingsView({ client }) {
  const formatAccountManager = () => {
    const staff = client?.assigned_staff || 'Unassigned';
    const parts = staff.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1]?.charAt(0)}.`;
    }
    return staff;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      <PageHeader 
        title="Settings" 
        description="Manage your account settings"
        icon={Settings}
      />

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Account Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsInfoCard 
            icon={Building2}
            label="Business Name"
            value={client?.business_name}
          />
          <SettingsInfoCard 
            icon={User}
            label="Contact Name"
            value={`${client?.primary_contact_first_name || ''} ${client?.primary_contact_last_name || ''}`}
          />
          <SettingsInfoCard 
            icon={Mail}
            label="Email"
            value={client?.primary_contact_email}
          />
          <SettingsInfoCard 
            icon={Target}
            label="Client ID"
            value={client?.short_id}
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-6">Digital Properties</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsInfoCard 
            icon={ExternalLink}
            label="Website"
            value={client?.digital_properties?.primary_website_url || 'Not set'}
          />
          <SettingsInfoCard 
            icon={BarChart3}
            label="Google Ads ID"
            value={client?.digital_properties?.google_ads_account_id || 'Not set'}
          />
          <SettingsInfoCard 
            icon={Activity}
            label="Meta Business ID"
            value={client?.digital_properties?.meta_business_manager_id || 'Not set'}
          />
          <SettingsInfoCard 
            icon={FileText}
            label="Analytics Property"
            value={client?.digital_properties?.google_analytics_property_id || 'Not set'}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <p className="text-sm text-slate-600">
          To update your account information, please contact your account manager.
        </p>
      </div>
    </motion.div>
  );
}

// ============ SETTINGS INFO CARD ============
function SettingsInfoCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-slate-800 truncate">{value || '—'}</p>
      </div>
    </div>
  );
}
